import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

// "invoice" = fatura emitida ao cliente · "credit_note" = nota de crédito.
export type ClientInvoiceKind = "invoice" | "credit_note";

// Antiguidade de saldo de uma fatura em aberto (vs. data de referência).
export type AgingBucket =
  | "current"
  | "d0_30"
  | "d31_60"
  | "d61_90"
  | "d90_plus";

// Risco de crédito do cliente (regra-based) [3.6.3].
export type RiskLevel = "green" | "yellow" | "red";

// Fase sugerida do workflow de cobrança [3.6.2].
export type CollectionStage = "pre_due" | "due_notice" | "soft" | "firm";

export interface ClientInvoice {
  id: number;
  clientId: number;
  clientName: string;
  workId?: number | null;
  workName?: string | null;
  billingMilestoneId?: number | null;
  kind: ClientInvoiceKind;
  originalInvoiceId?: number | null;
  originalInvoiceNumber?: string | null;
  number: string;
  statusId: number;
  statusCode: string;
  statusName: string;
  issueDate?: string | null;
  dueDate?: string | null;
  baseAmount?: number | null;
  vatAmount?: number | null;
  totalAmount?: number | null;
  paidAmount: number;
  openAmount: number;
  currencyCode: string;
  description?: string | null;
  notes?: string | null;
  ageDays?: number | null;
  agingBucket?: AgingBucket | null;
  // [3.6.6] Migração JAL para conta corrente — quando preenchido, a fatura sai do
  // radar de cobrança e mostra "Sob acordo" (continua a contar como dívida/aging).
  migratedToAccountAt?: string | null;
  migratedBy?: string | null;
  migrationNotes?: string | null;
  isMigratedToAccount: boolean;
  // [3.6.4] Motivo da autorização registado quando a emissão ultrapassou um bloqueio.
  blockOverrideReason?: string | null;
  created: string;
  lastModified: string;
}

export interface ClientInvoiceStatus {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
}

export interface ClientAgingRow {
  clientId: number;
  clientName: string;
  totalOutstanding: number;
  current: number;
  d0_30: number;
  d31_60: number;
  d61_90: number;
  d90Plus: number;
  invoiceCount: number;
}

export interface ReceivablesAging {
  asOf: string;
  totalOutstanding: number;
  currentTotal: number;
  bucket0_30: number;
  bucket31_60: number;
  bucket61_90: number;
  bucket90Plus: number;
  clients: ClientAgingRow[];
}

export interface ClientAccount {
  clientId: number;
  clientName: string;
  taxId?: string | null;
  outstandingBalance: number;
  openInvoiceCount: number;
  invoices: ClientInvoice[];
  installmentAmount?: number | null;
  frequency?: string | null;
  agreementStartDate?: string | null;
  agreementEndDate?: string | null;
  agreementNotes?: string | null;
  hasLegalProcess: boolean;
  legalProcessNotes?: string | null;
  // [3.6.x] Controlo de crédito — limite de crédito + risco já calculado pelo backend.
  creditLimit?: number | null;
  riskLevel: RiskLevel;
  riskReasons: string[];
}

// Risco de crédito de um cliente (GET /api/clients/{id}/risk) [3.6.3].
export interface ClientRisk {
  clientId: number;
  level: RiskLevel;
  overdueAmount: number;
  worstOverdueDays: number;
  outstandingBalance: number;
  openInvoiceCount: number;
  reasons: string[];
}

// Linha do painel de cobrança (uma por fatura emitida em aberto, não migrada) [3.6.2].
export interface CollectionDashboardRow {
  clientInvoiceId: number;
  number: string;
  clientId: number;
  clientName: string;
  clientEmail?: string | null;
  workName?: string | null;
  totalAmount?: number | null;
  openAmount: number;
  dueDate?: string | null;
  ageDays?: number | null;
  agingBucket?: AgingBucket | null;
  suggestedStage?: string | null;
  suggestedStageName?: string | null;
  lastActivityStage?: string | null;
  lastActivityDate?: string | null;
  riskLevel: RiskLevel;
}

// Painel de cobrança (GET /api/collections/dashboard) [3.6.2].
export interface CollectionDashboard {
  asOf: string;
  totalOpen: number;
  totalOverdue: number;
  overdueInvoiceCount: number;
  stageCounts: {
    pre_due: number;
    due_notice: number;
    soft: number;
    firm: number;
  };
  rows: CollectionDashboardRow[];
}

// Ação de cobrança registada (GET/POST /api/collections/activities, POST send-email).
export interface CollectionActivity {
  id: number;
  clientId: number;
  clientName?: string | null;
  clientInvoiceId?: number | null;
  invoiceNumber?: string | null;
  stage: string;
  stageName: string;
  channel: string;
  channelName: string;
  activityDate: string;
  subject?: string | null;
  body?: string | null;
  outcome?: string | null;
  emailTo?: string | null;
  emailStatus?: string | null;
  createdBy?: string | null;
  created: string;
}

// Pré-visualização do email de cobrança (GET /api/collections/email-preview).
export interface CollectionEmail {
  to?: string | null;
  subject: string;
  body: string;
  stage: string;
  stageName: string;
  clientInvoiceId: number;
}

// Manual create / edit payload (mirrors ClientInvoiceDto field names; Kind/Number/Status
// are server-assigned for a manual draft).
export interface ClientInvoicePayload {
  clientId: number;
  workId?: number | null;
  issueDate?: string | null;
  dueDate?: string | null;
  baseAmount?: number | null;
  vatAmount?: number | null;
  totalAmount?: number | null;
  description?: string | null;
  notes?: string | null;
  currencyCode?: string | null;
}

export interface RegisterPaymentPayload {
  amount: number;
  paymentDate?: string | null;
}

export interface ChangeStatusPayload {
  statusCode: string;
  // [3.6.4] Quando a emissão é bloqueada por incumprimento, reenviar com
  // overrideBlock=true + um motivo autoriza a emissão.
  overrideBlock?: boolean;
  overrideReason?: string;
}

export interface CreateCreditNotePayload {
  amount: number;
  baseAmount?: number | null;
  vatAmount?: number | null;
  description?: string | null;
  issueDate?: string | null;
}

export interface ClientAccountPayload {
  installmentAmount?: number | null;
  frequency?: string | null;
  agreementStartDate?: string | null;
  agreementEndDate?: string | null;
  agreementNotes?: string | null;
  hasLegalProcess: boolean;
  legalProcessNotes?: string | null;
  creditLimit?: number | null;
}

export interface MigrateInvoicePayload {
  migrationNotes?: string | null;
}

export interface CollectionDashboardFilters {
  clientId?: number | null;
  stage?: string | null;
  overdueOnly?: boolean | null;
  asOf?: string | null;
}

export interface CollectionActivityFilters {
  clientId?: number | null;
  clientInvoiceId?: number | null;
}

export interface LogCollectionActivityPayload {
  clientId: number;
  clientInvoiceId?: number | null;
  stage: string;
  channel: string;
  outcome?: string | null;
  activityDate?: string | null;
}

export interface SendCollectionEmailPayload {
  clientInvoiceId: number;
  stage: string;
  to?: string | null;
  subject?: string | null;
  body?: string | null;
}

export interface ClientInvoiceFilters {
  clientId?: number | null;
  workId?: number | null;
  status?: string | null; // status code
  kind?: ClientInvoiceKind | null;
  open?: boolean | null;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    let message = text || response.statusText;
    if (text) {
      try {
        const problem = JSON.parse(text) as { detail?: string; title?: string };
        message = problem.detail || problem.title || text;
      } catch {
        // Body is not JSON ProblemDetails — keep the raw text.
      }
    }
    throw new ApiError(response.status, message);
  }

  return (await response.json()) as T;
}

function jsonBody(payload: unknown): RequestInit {
  return {
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
}

export function getClientInvoices(
  filters?: ClientInvoiceFilters,
): Promise<ClientInvoice[]> {
  const params = new URLSearchParams();
  if (filters?.clientId != null && Number.isFinite(filters.clientId)) {
    params.set("clientId", String(filters.clientId));
  }
  if (filters?.workId != null && Number.isFinite(filters.workId)) {
    params.set("workId", String(filters.workId));
  }
  if (filters?.status) params.set("status", filters.status);
  if (filters?.kind) params.set("kind", filters.kind);
  if (filters?.open != null) params.set("open", String(filters.open));
  const query = params.toString();
  return request<ClientInvoice[]>(
    `/api/client-invoices${query ? `?${query}` : ""}`,
  );
}

export function getClientInvoiceStatuses(): Promise<ClientInvoiceStatus[]> {
  return request<ClientInvoiceStatus[]>("/api/client-invoices/statuses");
}

export function getReceivablesAging(asOf?: string): Promise<ReceivablesAging> {
  const query = asOf ? `?asOf=${encodeURIComponent(asOf)}` : "";
  return request<ReceivablesAging>(`/api/client-invoices/aging${query}`);
}

export function getClientInvoice(id: number): Promise<ClientInvoice> {
  return request<ClientInvoice>(`/api/client-invoices/${id}`);
}

export function createClientInvoice(
  payload: ClientInvoicePayload,
): Promise<ClientInvoice> {
  return request<ClientInvoice>("/api/client-invoices", {
    method: "POST",
    ...jsonBody(payload),
  });
}

export function updateClientInvoice(
  id: number,
  payload: ClientInvoicePayload,
): Promise<ClientInvoice> {
  return request<ClientInvoice>(`/api/client-invoices/${id}`, {
    method: "PUT",
    ...jsonBody({ ...payload, id }),
  });
}

export async function deleteClientInvoice(id: number): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/api/client-invoices/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) {
    const text = await response.text();
    let message = text || response.statusText;
    if (text) {
      try {
        const problem = JSON.parse(text) as { detail?: string; title?: string };
        message = problem.detail || problem.title || text;
      } catch {
        // Body is not JSON ProblemDetails — keep the raw text.
      }
    }
    throw new ApiError(response.status, message);
  }
}

export function registerPayment(
  id: number,
  payload: RegisterPaymentPayload,
): Promise<ClientInvoice> {
  return request<ClientInvoice>(`/api/client-invoices/${id}/payment`, {
    method: "POST",
    ...jsonBody(payload),
  });
}

export function changeStatus(
  id: number,
  payload: ChangeStatusPayload,
): Promise<ClientInvoice> {
  return request<ClientInvoice>(`/api/client-invoices/${id}/status`, {
    method: "POST",
    ...jsonBody(payload),
  });
}

export function createCreditNote(
  id: number,
  payload: CreateCreditNotePayload,
): Promise<ClientInvoice> {
  return request<ClientInvoice>(`/api/client-invoices/${id}/credit-note`, {
    method: "POST",
    ...jsonBody(payload),
  });
}

// [3.7.1] Gera um rascunho de fatura a partir de um marco de faturação.
export function generateInvoiceFromMilestone(
  milestoneId: number,
): Promise<ClientInvoice> {
  return request<ClientInvoice>(
    `/api/billingmilestones/${milestoneId}/generate-invoice`,
    { method: "POST" },
  );
}

// [3.6.5] Conta corrente do cliente (saldo + acordo + flag jurídico + faturas).
export function getClientAccount(clientId: number): Promise<ClientAccount> {
  return request<ClientAccount>(`/api/clients/${clientId}/account`);
}

export function saveClientAccount(
  clientId: number,
  payload: ClientAccountPayload,
): Promise<ClientAccount> {
  return request<ClientAccount>(`/api/clients/${clientId}/account`, {
    method: "PUT",
    ...jsonBody(payload),
  });
}

// [3.6.3] Risco de crédito do cliente (regra-based, calculado no backend).
export function getClientRisk(clientId: number): Promise<ClientRisk> {
  return request<ClientRisk>(`/api/clients/${clientId}/risk`);
}

// [3.6.6] Migra uma fatura (vencida há ≥ JAL dias) para a conta corrente do cliente.
export function migrateInvoiceToAccount(
  id: number,
  payload: MigrateInvoicePayload,
): Promise<ClientInvoice> {
  return request<ClientInvoice>(
    `/api/client-invoices/${id}/migrate-to-account`,
    { method: "POST", ...jsonBody(payload) },
  );
}

// [3.6.2] Painel de cobrança: faturas emitidas em aberto (não migradas) com fase sugerida.
export function getCollectionDashboard(
  filters?: CollectionDashboardFilters,
): Promise<CollectionDashboard> {
  const params = new URLSearchParams();
  if (filters?.clientId != null && Number.isFinite(filters.clientId)) {
    params.set("clientId", String(filters.clientId));
  }
  if (filters?.stage) params.set("stage", filters.stage);
  if (filters?.overdueOnly != null) {
    params.set("overdueOnly", String(filters.overdueOnly));
  }
  if (filters?.asOf) params.set("asOf", filters.asOf);
  const query = params.toString();
  return request<CollectionDashboard>(
    `/api/collections/dashboard${query ? `?${query}` : ""}`,
  );
}

// [3.6.2] Histórico de ações de cobrança (por cliente e/ou fatura), desc por data.
export function getCollectionActivities(
  filters?: CollectionActivityFilters,
): Promise<CollectionActivity[]> {
  const params = new URLSearchParams();
  if (filters?.clientId != null && Number.isFinite(filters.clientId)) {
    params.set("clientId", String(filters.clientId));
  }
  if (
    filters?.clientInvoiceId != null &&
    Number.isFinite(filters.clientInvoiceId)
  ) {
    params.set("clientInvoiceId", String(filters.clientInvoiceId));
  }
  const query = params.toString();
  return request<CollectionActivity[]>(
    `/api/collections/activities${query ? `?${query}` : ""}`,
  );
}

// [3.6.2] Regista manualmente uma ação de cobrança (chamada, nota, carta…).
export function logCollectionActivity(
  payload: LogCollectionActivityPayload,
): Promise<CollectionActivity> {
  return request<CollectionActivity>(`/api/collections/activities`, {
    method: "POST",
    ...jsonBody(payload),
  });
}

// [3.6.2] Compõe (sem enviar) o email de cobrança para uma fatura numa dada fase.
export function previewCollectionEmail(
  clientInvoiceId: number,
  stage: string,
): Promise<CollectionEmail> {
  const params = new URLSearchParams();
  params.set("clientInvoiceId", String(clientInvoiceId));
  if (stage) params.set("stage", stage);
  return request<CollectionEmail>(
    `/api/collections/email-preview?${params.toString()}`,
  );
}

// [3.6.2] Envia (stub/log) o email de cobrança e regista a ação correspondente.
export function sendCollectionEmail(
  payload: SendCollectionEmailPayload,
): Promise<CollectionActivity> {
  return request<CollectionActivity>(`/api/collections/send-email`, {
    method: "POST",
    ...jsonBody(payload),
  });
}
